// src/utils/excelExport.js
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

/**
 * Genera un archivo Excel real (.xlsx) con datos tabulares y formato profesional
 *
 * @param {Object} config
 * @param {string} config.titulo - Titulo del reporte
 * @param {string} config.subtitulo - Subtitulo (ej: "Periodo: Enero - Diciembre 2026")
 * @param {string} config.nombreCondominio - Nombre del condominio
 * @param {Array} config.columnas - Array de { header: 'Nombre', key: 'nombre', width: 25 }
 * @param {Array} config.datos - Array de objetos con los datos
 * @param {Object} config.resumen - { totalIngresos, totalGastos, balance, ... }
 * @param {Array} config.datosMensuales - Para grafica: [{ mes: 'Ene', ingresos: 500, gastos: 200 }, ...]
 * @param {string} config.nombreArchivo - Nombre del archivo sin extension
 */
export const exportarExcelProfesional = (config) => {
  const {
    titulo = 'Reporte Financiero',
    subtitulo = '',
    nombreCondominio = 'UrbanQ',
    columnas = [],
    datos = [],
    resumen = {},
    datosMensuales = [],
    nombreArchivo = 'reporte'
  } = config;

  // Crear workbook
  const wb = XLSX.utils.book_new();

  // ===============================================
  // HOJA 1: DATOS DETALLADOS
  // ===============================================

  // Construir array de arrays para la hoja
  const wsData = [];

  // Fila 1: Titulo
  wsData.push([titulo]);
  // Fila 2: Subtitulo
  wsData.push([subtitulo || `Generado el ${new Date().toLocaleDateString('es-EC')}`]);
  // Fila 3: Condominio
  wsData.push([`Condominio: ${nombreCondominio}`]);
  // Fila vacia
  wsData.push([]);

  // Fila 5: Resumen financiero si existe
  if (resumen && Object.keys(resumen).length > 0) {
    wsData.push(['RESUMEN FINANCIERO']);
    if (resumen.totalIngresos !== undefined) {
      wsData.push(['Total Ingresos:', `$${parseFloat(resumen.totalIngresos || 0).toFixed(2)}`]);
    }
    if (resumen.totalGastos !== undefined) {
      wsData.push(['Total Gastos:', `$${parseFloat(resumen.totalGastos || 0).toFixed(2)}`]);
    }
    if (resumen.balance !== undefined) {
      wsData.push(['Balance:', `$${parseFloat(resumen.balance || 0).toFixed(2)}`]);
    }
    if (resumen.totalRecibos !== undefined) {
      wsData.push(['Total Recibos:', resumen.totalRecibos]);
    }
    wsData.push([]);
  }

  // Headers de la tabla
  const headerRow = columnas.map(c => c.header);
  wsData.push(headerRow);

  // Datos
  datos.forEach(row => {
    const dataRow = columnas.map(c => {
      const val = row[c.key];
      // Si es monto, formatear como numero
      if (c.key === 'monto' || c.key === 'amount') {
        return parseFloat(val || 0);
      }
      return val || '';
    });
    wsData.push(dataRow);
  });

  // Fila de total al final
  const montoColIndex = columnas.findIndex(c => c.key === 'monto' || c.key === 'amount');
  if (montoColIndex !== -1 && datos.length > 0) {
    const totalRow = new Array(columnas.length).fill('');
    totalRow[montoColIndex - 1] = 'TOTAL:';
    totalRow[montoColIndex] = datos.reduce((sum, r) => sum + parseFloat(r.monto || r.amount || 0), 0);
    wsData.push([]);
    wsData.push(totalRow);
  }

  const ws = XLSX.utils.aoa_to_sheet(wsData);

  // Aplicar anchos de columna
  ws['!cols'] = columnas.map(c => ({ wch: c.width || 15 }));

  // Merge para titulo (primera fila)
  if (columnas.length > 1) {
    ws['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: columnas.length - 1 } },  // Titulo
      { s: { r: 1, c: 0 }, e: { r: 1, c: columnas.length - 1 } },  // Subtitulo
      { s: { r: 2, c: 0 }, e: { r: 2, c: columnas.length - 1 } },  // Condominio
    ];
  }

  XLSX.utils.book_append_sheet(wb, ws, 'Detalle');

  // ===============================================
  // HOJA 2: RESUMEN MENSUAL (si hay datos mensuales)
  // ===============================================
  if (datosMensuales && datosMensuales.length > 0) {
    const wsMonthly = [];
    wsMonthly.push(['RESUMEN MENSUAL - ' + titulo]);
    wsMonthly.push([]);
    wsMonthly.push(['Mes', 'Ingresos ($)', 'Gastos ($)', 'Balance ($)']);

    datosMensuales.forEach(m => {
      wsMonthly.push([
        m.mes,
        parseFloat(m.ingresos || 0),
        parseFloat(m.gastos || 0),
        parseFloat((m.ingresos || 0) - (m.gastos || 0))
      ]);
    });

    // Totales
    wsMonthly.push([]);
    wsMonthly.push([
      'TOTALES',
      datosMensuales.reduce((s, m) => s + parseFloat(m.ingresos || 0), 0),
      datosMensuales.reduce((s, m) => s + parseFloat(m.gastos || 0), 0),
      datosMensuales.reduce((s, m) => s + parseFloat((m.ingresos || 0) - (m.gastos || 0)), 0)
    ]);

    const ws2 = XLSX.utils.aoa_to_sheet(wsMonthly);
    ws2['!cols'] = [{ wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }];

    if (wsMonthly[0].length > 1) {
      ws2['!merges'] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 3 } },
      ];
    }

    XLSX.utils.book_append_sheet(wb, ws2, 'Resumen Mensual');
  }

  // ===============================================
  // EXPORTAR
  // ===============================================
  const fechaStr = new Date().toISOString().split('T')[0];
  const fileName = `${nombreArchivo}_${fechaStr}.xlsx`;

  // Generar buffer y descargar
  const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  saveAs(blob, fileName);

  return true;
};

/**
 * Exportar listado de recibos de pago del usuario
 */
export const exportarRecibosExcel = (recibos, usuario, condominio) => {
  return exportarExcelProfesional({
    titulo: 'Recibos de Pago - Estado de Cuenta',
    subtitulo: `Residente: ${usuario?.nombre || usuario?.name || 'N/A'} | Apt: ${usuario?.apartamento || usuario?.apartment || 'N/A'}`,
    nombreCondominio: condominio || 'UrbanQ',
    columnas: [
      { header: 'N\u00ba Recibo', key: 'numero', width: 18 },
      { header: 'Fecha', key: 'fecha', width: 15 },
      { header: 'Concepto', key: 'concepto', width: 35 },
      { header: 'Monto ($)', key: 'monto', width: 15 },
      { header: 'Estado', key: 'estado', width: 12 }
    ],
    datos: recibos,
    resumen: {
      totalRecibos: recibos.length,
      totalIngresos: recibos.reduce((s, r) => s + parseFloat(r.monto || 0), 0)
    },
    nombreArchivo: `recibos_pago_${(usuario?.nombre || usuario?.name || 'usuario').replace(/\s/g, '_')}`
  });
};

/**
 * Exportar reporte de contabilidad del administrador
 */
export const exportarContabilidadExcel = (pagos, resumen, datosMensuales, nombreCondominio) => {
  return exportarExcelProfesional({
    titulo: 'Reporte de Contabilidad',
    subtitulo: `Periodo: ${new Date().getFullYear()}`,
    nombreCondominio: nombreCondominio || 'UrbanQ',
    columnas: [
      { header: 'Fecha', key: 'fechaFormateada', width: 15 },
      { header: 'Usuario', key: 'usuario', width: 25 },
      { header: 'Apartamento', key: 'apartamento', width: 15 },
      { header: 'Concepto', key: 'concepto', width: 30 },
      { header: 'Monto ($)', key: 'monto', width: 15 },
      { header: 'Estado', key: 'estado', width: 12 }
    ],
    datos: pagos,
    resumen: resumen,
    datosMensuales: datosMensuales,
    nombreArchivo: `contabilidad_${nombreCondominio || 'urbanq'}`
  });
};
